/**
 * Investment/Portfolio Tracker Component
 * Shows asset allocation, gain/loss, portfolio chart, and individual investment details
 * Works with both demo data and Supabase-backed API assets
 */
import React, { useState, useMemo } from 'react';
import {
  PieChart as PieChartIcon,
  BarChart3,
  DollarSign,
  Percent,
  ArrowUpRight,
  ArrowDownRight,
  Wallet
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';

const COLORS = ['#4F85FF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

const formatCurrency = (val) => {
  const num = parseFloat(val) || 0;
  return num >= 0
    ? `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : `-$${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const InvestmentTracker = ({ accounts = { assets: [], liabilities: [] }, formatCurrency: fmtCur }) => {
  const [view, setView] = useState('allocation'); // 'allocation' | 'performance'
  const fc = fmtCur || formatCurrency;

  // Categorize investment accounts
  const investments = useMemo(() => {
    const investKeywords = ['401k', '401(k)', 'retirement', 'ira', 'roth', 'invest', 'stock', 'crypto', 'etf', 'bond', 'portfolio', 'brokerage', 'robinhood', 'vanguard', 'fidelity', 'schwab'];
    const realEstateKeywords = ['home', 'house', 'property', 'real estate', 'land', 'equity'];

    return (accounts.assets || []).map(asset => {
      const nameLower = asset.name.toLowerCase();
      const isInvestment = investKeywords.some(k => nameLower.includes(k));
      const isRealEstate = realEstateKeywords.some(k => nameLower.includes(k));
      let category = 'Cash & Savings';
      if (isInvestment) category = 'Investments';
      else if (isRealEstate) category = 'Real Estate';

      // Get current value from snapshots or asset
      const currentValue = Number(asset.currentValue) || 0;
      const costBasis = Number(asset.costBasis ?? asset.cost_basis ?? asset.originalValue ?? currentValue) || currentValue;

      return {
        id: asset.id,
        name: asset.name,
        category,
        value: currentValue,
        costBasis,
        gainLoss: currentValue - costBasis,
        gainLossPercent: costBasis > 0 ? ((currentValue - costBasis) / costBasis) * 100 : 0,
        icon: category === 'Investments' ? '📈' : category === 'Real Estate' ? '🏠' : '💰'
      };
    }).filter(inv => inv.value > 0);
  }, [accounts.assets]);

  // Aggregate by category
  const categoryData = useMemo(() => {
    const groups = {};
    investments.forEach(inv => {
      if (!groups[inv.category]) {
        groups[inv.category] = { name: inv.category, value: 0, costBasis: 0, count: 0 };
      }
      groups[inv.category].value += inv.value;
      groups[inv.category].costBasis += inv.costBasis;
      groups[inv.category].count += 1;
    });
    return Object.values(groups).sort((a, b) => b.value - a.value);
  }, [investments]);

  const totalValue = useMemo(() => investments.reduce((sum, i) => sum + i.value, 0), [investments]);
  const totalCostBasis = useMemo(() => investments.reduce((sum, i) => sum + i.costBasis, 0), [investments]);
  const totalGainLoss = totalValue - totalCostBasis;
  const totalGainLossPercent = totalCostBasis > 0 ? (totalGainLoss / totalCostBasis) * 100 : 0;

  // Performance data (by individual investment)
  const performanceData = useMemo(() => {
    return investments
      .filter(inv => inv.costBasis > 0)
      .map(inv => ({
        name: inv.name.length > 15 ? inv.name.slice(0, 15) + '…' : inv.name,
        gainLoss: inv.gainLoss,
        gainLossPercent: inv.gainLossPercent
      }))
      .sort((a, b) => b.gainLoss - a.gainLoss);
  }, [investments]);

  if (investments.length === 0) {
    return (
      <div data-testid="investment-tracker" className="text-center py-12 px-4">
        <div className="w-16 h-16 bg-blue-50 dark:bg-blue-950/40 rounded-full flex items-center justify-center mx-auto mb-4">
          <Wallet className="w-8 h-8 text-blue-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Investment Tracking
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          Add investment accounts (stocks, 401k, real estate) to see your portfolio allocation,
          gain/loss tracking, and performance breakdown here.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="investment-tracker" className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-gray-500 dark:text-gray-400">Portfolio Value</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{fc(totalValue)}</p>
        </div>
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-500 dark:text-gray-400">Cost Basis</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{fc(totalCostBasis)}</p>
        </div>
        <div className={`p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
          <div className="flex items-center gap-2 mb-1">
            {totalGainLoss >= 0
              ? <ArrowUpRight className="w-4 h-4 text-emerald-500" />
              : <ArrowDownRight className="w-4 h-4 text-red-500" />
            }
            <span className="text-xs text-gray-500 dark:text-gray-400">Total Gain/Loss</span>
          </div>
          <p className={`text-lg font-bold ${totalGainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {totalGainLoss >= 0 ? '+' : ''}{fc(totalGainLoss)}
          </p>
        </div>
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <Percent className="w-4 h-4 text-purple-500" />
            <span className="text-xs text-gray-500 dark:text-gray-400">Return</span>
          </div>
          <p className={`text-lg font-bold ${totalGainLossPercent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {totalGainLossPercent >= 0 ? '+' : ''}{totalGainLossPercent.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <button
          data-testid="btn-allocation-view"
          onClick={() => setView('allocation')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            view === 'allocation'
              ? 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
              : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          <PieChartIcon className="w-4 h-4" />
          Allocation
        </button>
        <button
          data-testid="btn-performance-view"
          onClick={() => setView('performance')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            view === 'performance'
              ? 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
              : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          Performance
        </button>
      </div>

      {/* Charts */}
      {view === 'allocation' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Pie Chart */}
          <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Asset Allocation</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={50}
                    paddingAngle={2}
                  >
                    {categoryData.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(val) => fc(val)}
                    contentStyle={{
                      backgroundColor: 'var(--tooltip-bg)',
                      color: 'var(--tooltip-text)',
                      border: '1px solid var(--tooltip-border)',
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}
                    itemStyle={{ color: 'var(--tooltip-text)' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="mt-3 space-y-2">
              {categoryData.map((cat, idx) => (
                <div key={cat.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                    <span className="text-gray-700 dark:text-gray-300">{cat.name}</span>
                    <span className="text-xs text-gray-400">({cat.count})</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{fc(cat.value)}</span>
                    <span className="text-xs text-gray-400">
                      {totalValue > 0 ? ((cat.value / totalValue) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Investment List */}
          <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Holdings</h4>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {investments.sort((a, b) => b.value - a.value).map(inv => (
                <div key={inv.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{inv.icon}</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{inv.name}</p>
                      <p className="text-xs text-gray-400">{inv.category}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{fc(inv.value)}</p>
                    <div className={`flex items-center gap-0.5 text-xs justify-end ${inv.gainLoss >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {inv.gainLoss >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {inv.gainLoss >= 0 ? '+' : ''}{inv.gainLossPercent.toFixed(1)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {view === 'performance' && (
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Gain/Loss by Account</h4>
          {performanceData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceData} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => fc(v)} fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" fontSize={11} width={75} tickLine={false} axisLine={false} />
                  <Tooltip
                    formatter={(val) => fc(val)}
                    cursor={{ fill: 'rgba(79, 133, 255, 0.06)' }}
                    contentStyle={{
                      backgroundColor: 'var(--tooltip-bg)',
                      color: 'var(--tooltip-text)',
                      border: '1px solid var(--tooltip-border)',
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}
                    itemStyle={{ color: 'var(--tooltip-text)' }}
                  />
                  <Bar dataKey="gainLoss" radius={[0, 4, 4, 0]}>
                    {performanceData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.gainLoss >= 0 ? '#10B981' : '#EF4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">No cost basis data available for performance tracking.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default InvestmentTracker;
